/**
 * WCAG 2.x contrast helpers (JUB-101).
 *
 * Public event templates ship an arbitrary hex palette (`EventTemplateConfig`).
 * A palette's own "text"/"accent" token is not guaranteed to meet WCAG AA
 * against every surface it gets paired with — e.g. golden_serenity's own
 * `hero.buttonText` is only 2.14:1 on `hero.buttonBg` (see color.test.ts),
 * and `palette.text` is 4.17:1 on `palette.surface` (fails the 4.5:1 floor
 * for normal-size text even though it is fine for the large headings the
 * template already uses it for). Rather than hand-pick a per-template patch,
 * every color the RSVP dialog renders as text is routed through
 * `ensureAccessibleText`/`ensureAccessibleErrorColor` so any current or
 * future template's config is safe by construction, not by eyeballing it.
 */

type Rgb = [number, number, number];

function hexToRgb(hex: string): Rgb {
  const normalized = hex.replace("#", "");
  const expanded =
    normalized.length === 3
      ? normalized
          .split("")
          .map((char) => char + char)
          .join("")
      : normalized;
  const value = parseInt(expanded, 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

function relativeLuminance([r, g, b]: Rgb): number {
  const [rs, gs, bs] = [r, g, b].map((channel) => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/** WCAG relative-luminance contrast ratio between two hex colors, 1:1–21:1. */
export function getContrastRatio(hexA: string, hexB: string): number {
  const luminanceA = relativeLuminance(hexToRgb(hexA));
  const luminanceB = relativeLuminance(hexToRgb(hexB));
  const [lighter, darker] = luminanceA > luminanceB ? [luminanceA, luminanceB] : [luminanceB, luminanceA];
  return (lighter + 0.05) / (darker + 0.05);
}

const ACCESSIBLE_DARK = "#1a1a1a";
const ACCESSIBLE_LIGHT = "#ffffff";

/**
 * Returns `preferredHex` unchanged if it already meets `minRatio` against
 * `backgroundHex` (WCAG AA normal text = 4.5:1 by default); otherwise falls
 * back to whichever of near-black / white contrasts better against that
 * background. Guarantees the floor for any background a template supplies,
 * without needing to special-case individual templates.
 */
export function ensureAccessibleText(preferredHex: string, backgroundHex: string, minRatio = 4.5): string {
  if (getContrastRatio(preferredHex, backgroundHex) >= minRatio) return preferredHex;
  const darkRatio = getContrastRatio(ACCESSIBLE_DARK, backgroundHex);
  const lightRatio = getContrastRatio(ACCESSIBLE_LIGHT, backgroundHex);
  return darkRatio >= lightRatio ? ACCESSIBLE_DARK : ACCESSIBLE_LIGHT;
}

// Ordered lightest -> darkest so the result stays recognizably "error red"
// instead of collapsing to plain ink the way ensureAccessibleText's fallback
// would.
const ACCESSIBLE_REDS = ["#ef4444", "#dc2626", "#b91c1c", "#991b1b", "#7f1d1d"];

/** Picks the lightest red that still clears `minRatio` against `backgroundHex`. */
export function ensureAccessibleErrorColor(backgroundHex: string, minRatio = 4.5): string {
  for (const red of ACCESSIBLE_REDS) {
    if (getContrastRatio(red, backgroundHex) >= minRatio) return red;
  }
  return ACCESSIBLE_REDS[ACCESSIBLE_REDS.length - 1];
}
